import React, { useState } from 'react';
import { downloadCSV } from '_utils/csv-export';
import { formatReportDateTime } from '_utils/iata';

const headerStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'left',
    backgroundColor: '#f9fafb',
    fontWeight: 600,
    fontSize: '13px',
    color: '#374151',
    position: 'sticky',
    top: 0,
    zIndex: 2,
    boxShadow: '0 1px 0 0 #e5e7eb',
};

const cellStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: '13px',
    color: '#1f2937',
};

const getTierBandBgColor = (tierBand) => {
    if (!tierBand) return undefined;
    if (tierBand === 'No Show' || tierBand === 'No Data') return '#9ca3af';
    if (tierBand === 'No Assistance') return '#bbf7d0';
    if (tierBand.startsWith('>')) return '#fecaca';
    const m = String(tierBand).match(/≤(\d+)/);
    if (!m) return undefined;
    const n = parseInt(m[1], 10);
    if (n <= 25) return '#bbf7d0';
    if (n <= 45) return '#fef08a';
    if (n <= 60) return '#fed7aa';
    return '#fecaca';
};

const tableStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    minWidth: '1300px',
    tableLayout: 'auto',
};

const subTitleStyle = {
    fontSize: '13px',
    fontWeight: 500,
    color: '#6b7280',
    marginBottom: '6px',
    marginTop: '18px',
};

const modalOverlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
};

const modalContentStyle = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '24px',
    minWidth: '320px',
    maxWidth: '90vw',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
};

const DEFAULT_ARRIVAL_PREADVISED_TIER_BANDS = [
    { value: '≤5', label: '≤5 min' },
    { value: '≤10', label: '≤10 min' },
    { value: '≤20', label: '≤20 min' },
    { value: '≤30', label: '≤30 min' },
    { value: '≤45', label: '≤45 min' },
    { value: '≤60', label: '≤60 min' },
    { value: '>60', label: '>60 min' },
];
const DEFAULT_ARRIVAL_NONADVISED_TIER_BANDS = [
    { value: '≤25', label: '≤25 min' },
    { value: '≤35', label: '≤35 min' },
    { value: '≤45', label: '≤45 min' },
    { value: '≤60', label: '≤60 min' },
    { value: '≤75', label: '≤75 min' },
    { value: '>75', label: '>75 min' },
];

const AIRPORT_ARRIVAL_TIER_BANDS = {
    ORK: {
        preAdvised: [
            { value: '≤10', label: '≤10 min' },
            { value: '≤15', label: '≤15 min' },
            { value: '≤25', label: '≤25 min' },
            { value: '>25', label: '>25 min' },
        ],
        nonAdvised: [
            { value: '≤15', label: '≤15 min' },
            { value: '≤25', label: '≤25 min' },
            { value: '≤35', label: '≤35 min' },
            { value: '>35', label: '>35 min' },
        ],
    },
    // BFS uses DEFAULT_ARRIVAL_*_TIER_BANDS (same as backend DEFAULT_ARRIVAL_* + overflow)
};

const getArrivalTierBands = (preAdvised, iata) => {
    const airportConfig = AIRPORT_ARRIVAL_TIER_BANDS[iata];
    if (airportConfig) {
        return preAdvised === true ? airportConfig.preAdvised : airportConfig.nonAdvised;
    }
    return preAdvised === true ? DEFAULT_ARRIVAL_PREADVISED_TIER_BANDS : DEFAULT_ARRIVAL_NONADVISED_TIER_BANDS;
};

const TierBandOverrideModal = ({ rows, tierBands, open, onClose, onApply, isPatching }) => {
    const [selectedBand, setSelectedBand] = React.useState(null);
    const [reason, setReason] = React.useState('');

    React.useEffect(() => {
        if (open && rows?.length && tierBands?.length) {
            const currentBand = rows.length === 1 ? rows[0].tierBand : null;
            const isValidCurrent = currentBand && tierBands.some((b) => b.value === currentBand);
            setSelectedBand(isValidCurrent ? currentBand : tierBands[0].value);
            setReason('');
        }
    }, [open, rows, tierBands]);

    if (!open) return null;

    const reasonTrimmed = (reason || '').trim();
    const canApply = rows?.length > 0 && selectedBand && reasonTrimmed.length > 0 && !isPatching;

    const handleApply = () => {
        if (canApply && onApply) onApply(rows, selectedBand, reasonTrimmed);
    };

    return (
        <div style={modalOverlayStyle} onClick={isPatching ? undefined : onClose} role="dialog" aria-modal="true" aria-labelledby="tier-override-title">
            <div style={{ ...modalContentStyle, minWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
                <h2 id="tier-override-title" style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
                    Assign Tier Band
                </h2>
                <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#6b7280' }}>
                    {rows.length === 1
                        ? 'Manually assign this passenger to a tier band. This will override the calculated result.'
                        : `Manually assign these ${rows.length} passengers to a tier band. This will override the calculated results.`}
                </p>
                <div style={{ marginBottom: '16px' }}>
                    {(tierBands || []).map((band) => (
                        <label key={band.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer', fontSize: '14px' }}>
                            <input
                                type="radio"
                                name="slaBand"
                                value={band.value}
                                checked={selectedBand === band.value}
                                onChange={() => setSelectedBand(band.value)}
                            />
                            {band.label}
                        </label>
                    ))}
                </div>
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>
                        Reason <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Enter reason for override..."
                        required
                        rows={3}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            fontSize: '14px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            resize: 'vertical',
                            boxSizing: 'border-box',
                        }}
                    />
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center' }}>
                    {isPatching && (
                        <span style={{ marginRight: '8px', fontSize: '13px', color: '#6b7280' }}>Updating...</span>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isPatching}
                        style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            cursor: isPatching ? 'not-allowed' : 'pointer',
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            opacity: isPatching ? 0.6 : 1,
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={!canApply}
                        style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            cursor: canApply ? 'pointer' : 'not-allowed',
                            backgroundColor: '#3b82f6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            opacity: canApply ? 1 : 0.6,
                        }}
                    >
                        {isPatching ? 'Updating...' : 'Apply'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const RawDataSection = ({ title, data, timeKey, timeLabel, getTierBandBgColor, filenamePrefix = '', onOpenOverrideModal, iata }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const fmtDt = (value) => formatReportDateTime(value, iata);

    // clear selection when the report data is refreshed
    React.useEffect(() => {
        setSelectedIds(new Set());
    }, [data]);

    if (!data?.length) return null;

    const selectableRows = data.filter((row) => row.bookingId);
    const selectedRows = selectableRows.filter((row) => selectedIds.has(row.bookingId));
    const allSelected = selectableRows.length > 0 && selectedRows.length === selectableRows.length;

    const toggleRow = (bookingId) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(bookingId)) next.delete(bookingId);
            else next.add(bookingId);
            return next;
        });
    };

    const toggleAll = () => {
        setSelectedIds(allSelected ? new Set() : new Set(selectableRows.map((row) => row.bookingId)));
    };

    const handleExportCsv = () => {
        const rows = data.map((row, idx) => ({
            '#': idx + 1,
            'Passenger Name': row.name ?? '',
            'Passenger ID': row.passengerId ?? '',
            'Booking ID': row.bookingId ?? '',
            'Flight Number': row.flightNumber ?? '',
            'Date Created': fmtDt(row.dateCreated),
            [timeLabel]: fmtDt(row[timeKey]),
            'On Blocks Arrival': fmtDt(row.onBlocksArrivalTime),
            'Start Waiting Time': fmtDt(row.startWaitingTime),
            'At Location Time': fmtDt(row.atLocationTime),
            'First Resource At Location': fmtDt(row.firstResourceAtLocation),
            'At Location Source': row.atLocationSource ?? '',
            'Ramp Assistance': row.rampAssistance === true ? 'Yes' : 'No',
            'Used On Blocks For Start': row.usedOnBlocksForStart === true ? 'Yes' : 'No',
            'Wait (Mins)': row.waitMinutes != null ? row.waitMinutes : '',
            'Tier Band': row.tierBand ?? '',
            'Non-Preadvised Reason': row.nonPreadvisedReason ?? '',
            Override: row.overrideApplied ? 'Yes' : 'No',
            Reason: row.overrideReason ?? '',
            User: row.overrideUserName ?? '',
            Timestamp: row.overrideTimestamp ? fmtDt(row.overrideTimestamp) : '',
            'Rejected': row.rejected ? 'Pax Rejected' : '',
            Date: row.date ?? '',
        }));
        downloadCSV(rows, `${filenamePrefix}${title.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.csv`);
    };

    return (
        <div style={{ marginBottom: '16px' }}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    padding: '8px 0',
                }}
            >
                <span
                    style={{
                        display: 'inline-block',
                        fontSize: '12px',
                        color: '#9ca3af',
                        transition: 'transform 0.2s ease',
                        transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                >
                    &#9654;
                </span>
                <p style={{ ...subTitleStyle, marginTop: 0, marginBottom: 0 }}>
                    {title} ({data.length} bookings)
                </p>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleExportCsv(); }}
                    style={{
                        marginLeft: 'auto',
                        padding: '4px 10px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                    }}
                >
                    Export CSV
                </button>
                {selectedRows.length > 0 && onOpenOverrideModal && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onOpenOverrideModal(selectedRows); }}
                        style={{
                            padding: '4px 10px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            backgroundColor: '#3b82f6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                        }}
                    >
                        Assign Tier Band ({selectedRows.length})
                    </button>
                )}
            </div>
            {isOpen && (
                <div style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'auto', marginTop: '6px' }}>
                    <table style={tableStyle}>
                        <thead>
                            <tr>
                                {onOpenOverrideModal && (
                                    <th style={{ ...headerStyle, textAlign: 'center', width: '40px' }}>
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={toggleAll}
                                            disabled={!selectableRows.length}
                                            title="Select all"
                                        />
                                    </th>
                                )}
                                <th style={headerStyle}>#</th>
                                <th style={headerStyle}>Passenger Name</th>
                                <th style={{ ...headerStyle, minWidth: '220px' }}>Passenger ID</th>
                                <th style={{ ...headerStyle, minWidth: '220px' }}>Booking ID</th>
                                <th style={headerStyle}>Flight Number</th>
                                <th style={headerStyle}>Date Created</th>
                                <th style={headerStyle}>{timeLabel}</th>
                                <th style={headerStyle}>On Blocks</th>
                                <th style={headerStyle}>Start Waiting</th>
                                <th style={headerStyle}>At Location</th>
                                <th style={headerStyle}>First Resource At Location</th>
                                <th style={headerStyle}>At Location Source</th>
                                <th style={headerStyle}>Ramp Assistance</th>
                                <th style={headerStyle}>Used On Blocks</th>
                                <th style={headerStyle}>Wait (Mins)</th>
                                <th style={headerStyle}>Tier Band</th>
                                <th style={headerStyle}>Non-Preadvised Reason</th>
                                <th style={headerStyle}>Override</th>
                                <th style={headerStyle}>Reason</th>
                                <th style={headerStyle}>User</th>
                                <th style={headerStyle}>Timestamp</th>
                                <th style={headerStyle}>Rejected</th>
                                <th style={headerStyle}>Date</th>
                                <th style={{ ...headerStyle, textAlign: 'center' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, idx) => (
                                <tr
                                    key={row.bookingId ? `${row.bookingId}-${row.date}-${idx}` : idx}
                                    onClick={(e) => {
                                        if (!row.bookingId || !onOpenOverrideModal) return;
                                        if (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON') return;
                                        toggleRow(row.bookingId);
                                    }}
                                    style={{ cursor: row.bookingId && onOpenOverrideModal ? 'pointer' : undefined }}
                                >
                                    {onOpenOverrideModal && (
                                        <td style={{ ...cellStyle, textAlign: 'center' }}>
                                            {row.bookingId ? (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(row.bookingId)}
                                                    onChange={() => toggleRow(row.bookingId)}
                                                />
                                            ) : null}
                                        </td>
                                    )}
                                    <td style={cellStyle}>{idx + 1}</td>
                                    <td style={cellStyle}>{row.name || ''}</td>
                                    <td style={{ ...cellStyle, minWidth: '220px', fontSize: '11px', wordBreak: 'break-all' }}>{row.passengerId || ''}</td>
                                    <td style={{ ...cellStyle, minWidth: '220px' }}>{row.bookingId || ''}</td>
                                    <td style={cellStyle}>{row.flightNumber || ''}</td>
                                    <td style={cellStyle}>{fmtDt(row.dateCreated)}</td>
                                    <td style={cellStyle}>{fmtDt(row[timeKey])}</td>
                                    <td style={cellStyle}>{fmtDt(row.onBlocksArrivalTime)}</td>
                                    <td style={cellStyle}>{fmtDt(row.startWaitingTime)}</td>
                                    <td style={cellStyle}>{fmtDt(row.atLocationTime)}</td>
                                    <td style={cellStyle}>{fmtDt(row.firstResourceAtLocation)}</td>
                                    <td style={cellStyle}>{row.atLocationSource || ''}</td>
                                    <td style={cellStyle}>{row.rampAssistance ? 'Yes' : 'No'}</td>
                                    <td style={cellStyle}>{row.usedOnBlocksForStart ? 'Yes' : 'No'}</td>
                                    <td style={cellStyle}>{row.waitMinutes != null ? row.waitMinutes : ''}</td>
                                    <td style={{ ...cellStyle, backgroundColor: getTierBandBgColor(row.tierBand) }}>{row.tierBand || ''}</td>
                                    <td style={cellStyle}>{row.nonPreadvisedReason ?? '—'}</td>
                                    <td style={cellStyle}>{row.overrideApplied ? 'Yes' : 'No'}</td>
                                    <td style={cellStyle}>{row.overrideReason ?? '—'}</td>
                                    <td style={cellStyle}>{row.overrideUserName ?? '—'}</td>
                                    <td style={cellStyle}>{row.overrideTimestamp ? fmtDt(row.overrideTimestamp) : '—'}</td>
                                    <td style={{ ...cellStyle, backgroundColor: row.rejected ? '#fecaca' : undefined }}>{row.rejected ? 'Pax Rejected' : ''}</td>
                                    <td style={cellStyle}>{row.date || ''}</td>
                                    <td style={{ ...cellStyle, textAlign: 'center' }}>
                                        {row.bookingId && onOpenOverrideModal ? (
                                            <button
                                                type="button"
                                                onClick={() => onOpenOverrideModal([row])}
                                                style={{
                                                    padding: '6px 12px',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    backgroundColor: '#3b82f6',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                }}
                                            >
                                                Action
                                            </button>
                                        ) : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const EcacArrivalSummaryRawTable = ({ rawData = [], iata, overrideRows, onOverrideRowsChange, onPatchBookings, isPatching }) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleOpenOverrideModal = (rows) => onOverrideRowsChange?.(rows);
    const handleApplyOverride = (rows, slaBand, reason) => {
        const validRows = (rows || []).filter((row) => row?.bookingId);
        if (validRows.length && onPatchBookings) onPatchBookings(validRows, slaBand, reason);
    };

    if (!rawData?.length) {
        return null;
    }

    const preAdvisedData = rawData.filter((row) => row.preAdvised === true);
    const nonAdvisedData = rawData.filter((row) => row.preAdvised === false);

    return (
        <div
            style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
                marginTop: '12px',
                marginBottom: '12px',
                backgroundColor: '#fff',
            }}
        >
            <div
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                <span
                    style={{
                        display: 'inline-block',
                        fontSize: '12px',
                        color: '#9ca3af',
                        transition: 'transform 0.2s ease',
                        transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                >
                    &#9654;
                </span>
                <p style={{ ...subTitleStyle, marginTop: 0, marginBottom: 0 }}>
                    Raw ECAC Arrival Summary Data ({rawData.length} bookings)
                </p>
            </div>

            {isOpen && (
                <div style={{ marginTop: '10px' }}>
                    <RawDataSection
                        title="Pre-Advised"
                        data={preAdvisedData}
                        timeKey="arrivalTime"
                        timeLabel="Scheduled Arrival"
                        getTierBandBgColor={getTierBandBgColor}
                        filenamePrefix="ecac-arrival-"
                        onOpenOverrideModal={handleOpenOverrideModal}
                        iata={iata}
                    />
                    <RawDataSection
                        title="Non-Advised"
                        data={nonAdvisedData}
                        timeKey="arrivalTime"
                        timeLabel="Scheduled Arrival"
                        getTierBandBgColor={getTierBandBgColor}
                        filenamePrefix="ecac-arrival-"
                        onOpenOverrideModal={handleOpenOverrideModal}
                        iata={iata}
                    />
                </div>
            )}
            <TierBandOverrideModal
                rows={overrideRows || []}
                tierBands={overrideRows?.length ? getArrivalTierBands(overrideRows[0].preAdvised, iata) : []}
                open={!!overrideRows?.length}
                onClose={() => onOverrideRowsChange?.(null)}
                onApply={handleApplyOverride}
                isPatching={isPatching}
            />
        </div>
    );
};

export default EcacArrivalSummaryRawTable;
